import {
	Box,
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { AchievementCondition } from '@/api/enum/common.enum';
import { useFileUploader } from '@/hooks/useFileUploader';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { TaskAchievementCumulativeSeriesDTO } from '@/api/DTO/task/achievement.interface';
import {
	useCreateAchievementMutation,
	useUpdateAchievementMutation,
} from '@/api/task/achievementApiSlice';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
	type: FormType;
	details?: TaskAchievementCumulativeSeriesDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function AchievementInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createAchievement] = useCreateAchievementMutation();
	const [updateAchievement] = useUpdateAchievementMutation();

	const conditionList = [
		{
			id: AchievementCondition.MeditationTime,
			name: t('achievementList.MeditationTime'),
		},
		{ id: AchievementCondition.DayTotal, name: t('achievementList.DayTotal') },
		{
			id: AchievementCondition.FinishHealth,
			name: t('achievementList.FinishHealth'),
		},
		{
			id: AchievementCondition.UseIntegral,
			name: t('achievementList.UseIntegral'),
		},
		{
			id: AchievementCondition.FinishDay21,
			name: t('achievementList.FinishDay21'),
		},
		{
			id: AchievementCondition.FinishBody,
			name: t('achievementList.FinishBody'),
		},
		{
			id: AchievementCondition.FinishMeditation,
			name: t('achievementList.FinishMeditation'),
		},
		{
			id: AchievementCondition.FinishMajor,
			name: t('achievementList.FinishMajor'),
		},
		{
			id: AchievementCondition.FinishCrossover,
			name: t('achievementList.FinishCrossover'),
		},
		{
			id: AchievementCondition.HeartExercise,
			name: t('achievementList.HeartExercise'),
		},
		{
			id: AchievementCondition.DrawBlindbox,
			name: t('achievementList.DrawBlindbox'),
		},
		{
			id: AchievementCondition.FinishAllExercise,
			name: t('achievementList.FinishAllExercise'),
		},
		{
			id: AchievementCondition.IntegralTotal,
			name: t('achievementList.IntegralTotal'),
		},
	];

	const defaultValues: DefaultValues<TaskAchievementCumulativeSeriesDTO> = {};
	useEffect(() => {
		if (type == FormType.edit) {
			if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
			if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
			if (Number(details?.finishedCondition) > -1)
				methods.setValue('finishedCondition', details?.finishedCondition);
			if (details?.publishedStartAt)
				methods.setValue('publishedStartAt', details.publishedStartAt);
			if (details?.publishedEndAt)
				methods.setValue('publishedEndAt', details.publishedEndAt);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
		finishedCondition: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<TaskAchievementCumulativeSeriesDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<TaskAchievementCumulativeSeriesDTO> = async (
		data
	) => {
		const obj: TaskAchievementCumulativeSeriesDTO = { ...data };
		const response =
			type == FormType.create
				? await createAchievement(obj)
				: await updateAchievement({ ...obj, id: details?.id || '' });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createMeditationSuccess')
					: t('snackbar.editMeditationSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate('/task-manage/achievement-list');
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/task-manage/achievement-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('achievementList.seriesName')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={6}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('achievementList.seriesName')}
											maxLength={100}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDropDownMenu
									disabled={type == FormType.edit}
									control={methods.control}
									name='finishedCondition'
									label={t('achievementList.completionConditionsApp')}
									options={conditionList}
								/>
								<Typography
									variant='body2'
									sx={{
										display: 'flex',
									}}
								>
									&nbsp;
									{t('achievementList.completionConditionsTips')}
								</Typography>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('taskManage.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('taskManage.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{t('taskManage.tips')}
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
